/**
 * `getCitationTemplate` — resolves the active template for a given
 * `(juris, language)` bucket.
 *
 * Two-layer resolution:
 *   1. Lawyer override — if the caller supplies a `userOverrides` map
 *      with a matching `bucketKey`, return that (Settings UI lets the
 *      lawyer pick a different preset or author their own — P1.B).
 *   2. Default preset — falls through to `getDefaultPreset(juris, lang)`.
 *   3. Last resort — when no preset exists for the bucket (UK / US /
 *      EU / Gulf today), fall through to the FR NF Z44-005 preset so
 *      the renderer never crashes. The output will look "French" for
 *      a moment, but at least readable — better than an empty chip.
 *
 * Pure function. No React. No fetch. No storage access — the caller
 * (`useCitationTemplate` hook) is responsible for reading user
 * overrides from wherever they live (localStorage today, BE-synced
 * tomorrow).
 */
import type {
  CitationJurisdiction,
  CitationLanguage,
  CitationTemplate,
} from '../entity'
import { PRESETS, bucketKey, getDefaultPreset } from '../presets'

export interface GetCitationTemplateInput {
  juris: CitationJurisdiction
  language: CitationLanguage
  /** Optional user override map, keyed by `bucketKey(juris, lang)`.
   *  Supply the full template (not just the id) so the function stays
   *  pure — caller resolves persistence (localStorage today). */
  userOverrides?: Readonly<Record<string, CitationTemplate>>
}

/** Last-resort fallback when no `(juris, lang)` preset exists. The
 *  product surfaces lots of juris combos that don't have a corpus
 *  yet — fall through to FR NF Z44-005 so the chip never blanks out. */
const FALLBACK_TEMPLATE: CitationTemplate =
  PRESETS.find((p) => p.id === 'fr-fr-nf-z44-005') ?? PRESETS[0]

export function getCitationTemplate(
  input: GetCitationTemplateInput,
): CitationTemplate {
  const { juris, language, userOverrides } = input
  const key = bucketKey(juris, language)
  if (userOverrides?.[key]) return userOverrides[key]
  const preset = getDefaultPreset(juris, language)
  if (preset) return preset
  return FALLBACK_TEMPLATE
}
